import { navigate } from '@/lib/nav';
import { useState } from 'react';

interface EditorToolbarProps {
  mode: 'read' | 'edit';
  onToggleMode: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onToggleHistory: () => void;
  historyOpen: boolean;
  onToggleAi: () => void;
  aiOpen: boolean;
  onToggleTemplatePicker: () => void;
  templatePickerOpen: boolean;
  currentTemplateLabel: string;
  onExportPdf: () => void;
  onExportPng: () => void;
  onSaveVersion: () => void;
  onShowVersions: () => void;
  onShowLibrary: () => void;
  ficheIdShort: string;
}

export function EditorToolbar({
  mode,
  onToggleMode,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onToggleHistory,
  historyOpen,
  onToggleAi,
  aiOpen,
  onToggleTemplatePicker,
  templatePickerOpen,
  currentTemplateLabel,
  onExportPdf,
  onExportPng,
  onSaveVersion,
  onShowVersions,
  onShowLibrary,
  ficheIdShort,
}: EditorToolbarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <div className="bg-ink text-paper py-2 px-3 sm:px-4 flex items-center justify-between font-hand text-[13px] sm:text-[14px] gap-2 flex-wrap">
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="hover:underline underline-offset-4 min-h-[36px] flex items-center"
          aria-label="Retour à la création de fiche"
        >
          ← <span className="hidden xs:inline">Nouvelle </span>fiche
        </button>
        <button
          type="button"
          onClick={onShowLibrary}
          className="hover:underline underline-offset-4 min-h-[36px] flex items-center"
          data-testid="library-link"
        >
          📚 <span className="hidden xs:inline">Bibliothèque</span>
        </button>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Annuler (Ctrl+Z)"
          title="Annuler (Ctrl+Z)"
          className="px-2 py-1 min-h-[32px] border border-paper/30 rounded-control hover:bg-paper/10 disabled:opacity-30"
          data-testid="undo-button"
        >
          ↶ <span className="hidden sm:inline">Annuler</span>
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Refaire (Ctrl+Shift+Z)"
          title="Refaire (Ctrl+Shift+Z)"
          className="px-2 py-1 min-h-[32px] border border-paper/30 rounded-control hover:bg-paper/10 disabled:opacity-30"
          data-testid="redo-button"
        >
          ↷ <span className="hidden sm:inline">Refaire</span>
        </button>
        <button
          type="button"
          onClick={onToggleAi}
          aria-pressed={aiOpen}
          aria-label="Demander à l'IA"
          className={[
            'px-2.5 sm:px-3 py-1 min-h-[32px] rounded-control border-[2px] transition-colors flex items-center gap-1',
            aiOpen
              ? 'bg-accent-blue text-paper border-paper'
              : 'bg-transparent text-paper border-paper/30 hover:bg-paper/10',
          ].join(' ')}
          data-testid="ai-toggle"
        >
          🤖 IA
        </button>
        <button
          type="button"
          onClick={onToggleMode}
          aria-pressed={mode === 'edit'}
          className={[
            'px-2.5 sm:px-3 py-1 min-h-[32px] rounded-control border-[2px] transition-colors',
            mode === 'edit'
              ? 'bg-accent-yellow text-ink border-paper'
              : 'bg-transparent text-paper border-paper/30 hover:bg-paper/10',
          ].join(' ')}
          data-testid="mode-toggle"
        >
          {mode === 'edit' ? '✅ Terminé' : '✏️ Éditer'}
        </button>
        <button
          type="button"
          onClick={onToggleTemplatePicker}
          aria-pressed={templatePickerOpen}
          aria-label="Changer de template"
          className={[
            'px-2.5 sm:px-3 py-1 min-h-[32px] rounded-control border-[2px] transition-colors flex items-center gap-1',
            templatePickerOpen
              ? 'bg-accent-yellow text-ink border-paper'
              : 'bg-transparent text-paper border-paper/30 hover:bg-paper/10',
          ].join(' ')}
          data-testid="template-toggle"
          title={`Template : ${currentTemplateLabel}`}
        >
          🎨 <span className="hidden sm:inline">{currentTemplateLabel}</span>
        </button>
        <button
          type="button"
          onClick={onToggleHistory}
          aria-pressed={historyOpen}
          aria-label="Historique"
          className={[
            'px-2 py-1 min-h-[32px] border border-paper/30 rounded-control hover:bg-paper/10',
            historyOpen ? 'bg-paper/10' : '',
          ].join(' ')}
          data-testid="history-toggle"
        >
          🕓
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-expanded={moreOpen}
            aria-label="Plus d'actions"
            className="px-2 py-1 min-h-[32px] border border-paper/30 rounded-control hover:bg-paper/10"
            data-testid="more-toggle"
          >
            ⬇️
          </button>
          {moreOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full mt-1 bg-white text-ink border-2 border-ink rounded-control shadow-ink min-w-[200px] z-50"
              data-testid="more-menu"
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreOpen(false);
                  onExportPdf();
                }}
                className="block w-full text-left px-3 py-2 hover:bg-accent-blue/10 text-[14px]"
                data-testid="export-pdf"
              >
                📄 Exporter PDF
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreOpen(false);
                  onExportPng();
                }}
                className="block w-full text-left px-3 py-2 hover:bg-accent-blue/10 text-[14px] border-t border-ink/15"
                data-testid="export-png"
              >
                🖼️ Exporter PNG
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreOpen(false);
                  onSaveVersion();
                }}
                className="block w-full text-left px-3 py-2 hover:bg-accent-blue/10 text-[14px] border-t border-ink/15"
                data-testid="save-version"
              >
                💾 Sauver une version
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreOpen(false);
                  onShowVersions();
                }}
                className="block w-full text-left px-3 py-2 hover:bg-accent-blue/10 text-[14px] border-t border-ink/15"
                data-testid="show-versions"
              >
                🗂️ Voir les versions
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <span className="font-mono text-[12px] opacity-60">{ficheIdShort}</span>
    </div>
  );
}
